import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getRiskTemplates, createRiskTemplate, updateRiskTemplate, deleteRiskTemplate,
  getDimensions,
} from '../../api/admin';
import AdminLayout from '../../components/AdminLayout';
import Button from '../../components/Button';
import Alert from '../../components/Alert';

// V4 F.4 / M23: 风险模板编辑后台
// 后端: apps/enterprises/admin_risk_template_views.py (IsAdminRole = admin/superadmin)
// - 模板按 (industry, dimension_code) 唯一; 同维度可有多个 industry 版本（通用 + 行业覆盖）
// - 占位符白名单 {name} {score} {position}（_SafeFormatter, 缺学员属性时静默回退）
// - 写操作清空 v4:class_risks:* 缓存 (5min TTL 反正不长)

const LEVELS = ['极高', '高', '中', '低'];
// 设计文档第 7.5 节 + seed migration 0008 的行业集
const INDUSTRY_SUGGESTIONS = ['通用', '金融', '教育', '制造', '互联网', '医疗', '零售'];

const LEVEL_COLOR = {
  极高: { bg: '#fee2e2', fg: '#991b1b' },
  高:   { bg: '#fef3c7', fg: '#92400e' },
  中:   { bg: '#e0f2fe', fg: '#075985' },
  低:   { bg: '#f1f5f9', fg: '#475569' },
};

const EMPTY_FORM = {
  industry: '',
  dimension_code: '',
  threshold: 60,
  level: '高',
  template_text: '',
  is_active: true,
};

export default function AdminRiskTemplatesPage() {
  const [list, setList] = useState([]);
  const [dimensions, setDimensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 过滤
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterDimCode, setFilterDimCode] = useState('');
  const [filterActive, setFilterActive] = useState('');  // '', 'true', 'false'

  // 表单
  const [editingId, setEditingId] = useState(null);  // null=关 / 'new' / number
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const isCreating = editingId === 'new';

  const dimMap = useMemo(() => {
    const m = {};
    dimensions.forEach((d) => { m[d.code] = d.name; });
    return m;
  }, [dimensions]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filterIndustry) params.industry = filterIndustry;
      if (filterDimCode) params.dimension_code = filterDimCode;
      if (filterActive) params.is_active = filterActive;
      const { data } = await getRiskTemplates(params);
      setList(Array.isArray(data) ? data : (data.results ?? []));
    } catch (e) {
      setError(e.response?.data?.detail || '加载失败');
    } finally {
      setLoading(false);
    }
  }, [filterIndustry, filterDimCode, filterActive]);

  useEffect(() => {
    // 维度列表只拉一次, 用于 dim_code 下拉
    getDimensions().then(({ data }) => {
      setDimensions(Array.isArray(data) ? data : (data.results ?? []));
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm({
      ...EMPTY_FORM,
      industry: filterIndustry || '通用',
      dimension_code: filterDimCode || (dimensions[0]?.code || ''),
    });
    setEditingId('new');
    setError('');
    setSuccess('');
  };

  const openEdit = (t) => {
    setForm({
      industry: t.industry,
      dimension_code: t.dimension_code,
      threshold: t.threshold,
      level: t.level,
      template_text: t.template_text,
      is_active: t.is_active,
    });
    setEditingId(t.id);
    setError('');
    setSuccess('');
  };

  const closeForm = () => { setEditingId(null); setForm(EMPTY_FORM); };

  const validate = () => {
    if (!form.industry?.trim()) return '行业不能为空';
    if (!form.dimension_code) return '必须选择维度';
    if (form.threshold < 0 || form.threshold > 100) return '阈值必须在 0-100 之间';
    if (!LEVELS.includes(form.level)) return '风险等级非法';
    if (!form.template_text?.trim()) return '模板文本不能为空';
    // 占位符白名单本地预校验, 与后端 ALLOWED_PLACEHOLDERS 对齐
    const matches = form.template_text.match(/\{(\w+)\}/g) || [];
    const ALLOWED = new Set(['name', 'score', 'position']);
    const unknown = matches
      .map((m) => m.slice(1, -1))
      .filter((k) => !ALLOWED.has(k));
    if (unknown.length) return `未支持的占位符: ${[...new Set(unknown)].join(', ')}（仅支持 name / score / position）`;
    return null;
  };

  const handleSave = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setSaving(true); setError('');
    try {
      if (isCreating) {
        await createRiskTemplate(form);
        setSuccess(`已创建：[${form.industry}] ${dimMap[form.dimension_code] || form.dimension_code}`);
      } else {
        await updateRiskTemplate(editingId, form);
        setSuccess('已保存');
      }
      closeForm();
      load();
    } catch (e) {
      const data = e.response?.data || {};
      const firstField = Object.keys(data).find((k) => Array.isArray(data[k]) && data[k][0]);
      const msg = firstField ? `${firstField}: ${data[firstField][0]}` : (data.detail || '保存失败');
      setError(msg);
    } finally { setSaving(false); }
  };

  const handleToggleActive = async (t) => {
    try {
      await updateRiskTemplate(t.id, { is_active: !t.is_active });
      setSuccess(`已${t.is_active ? '停用' : '启用'}：[${t.industry}] ${dimMap[t.dimension_code] || t.dimension_code}`);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || '操作失败');
    }
  };

  const handleDelete = async (t) => {
    const dimName = dimMap[t.dimension_code] || t.dimension_code;
    if (!window.confirm(`确认删除模板？\n\n[${t.industry}] ${dimName}\n\n删除后该 (行业, 维度) 风险将不再触发，直至重新创建。`)) return;
    try {
      await deleteRiskTemplate(t.id);
      setSuccess(`已删除：[${t.industry}] ${dimName}`);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || '删除失败');
    }
  };

  return (
    <AdminLayout title="风险模板">
      {error && <Alert type="error" style={{ marginBottom: 12 }}>{error}</Alert>}
      {success && <Alert type="success" style={{ marginBottom: 12 }}>{success}</Alert>}

      <div style={{ marginBottom: 12, fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
        风险模板按 (行业, 维度) 配置阈值与文案。学员该维度得分 &lt; 阈值时触发风险项，
        优先匹配企业行业；命中失败 fallback 到 <code style={inlineCode}>通用</code>。
        模板支持 <code style={inlineCode}>{'{name}'}</code> / <code style={inlineCode}>{'{score}'}</code> / <code style={inlineCode}>{'{position}'}</code> 三个占位符（缺值时静默回退为空串）。
      </div>

      {/* 过滤器 */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={labelStyle}>行业</label>
          <input list="industry-suggestions" style={{ ...inputStyle, width: 140 }}
            value={filterIndustry} onChange={(e) => setFilterIndustry(e.target.value)}
            placeholder="全部" />
          <datalist id="industry-suggestions">
            {INDUSTRY_SUGGESTIONS.map((i) => <option key={i} value={i} />)}
          </datalist>
        </div>
        <div>
          <label style={labelStyle}>维度</label>
          <select style={{ ...inputStyle, width: 200 }}
            value={filterDimCode} onChange={(e) => setFilterDimCode(e.target.value)}>
            <option value="">全部</option>
            {dimensions.map((d) => (
              <option key={d.code} value={d.code}>{d.name}（{d.code}）</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>状态</label>
          <select style={{ ...inputStyle, width: 100 }}
            value={filterActive} onChange={(e) => setFilterActive(e.target.value)}>
            <option value="">全部</option>
            <option value="true">启用</option>
            <option value="false">停用</option>
          </select>
        </div>
        <div style={{ flex: 1 }} />
        <Button onClick={openNew}>+ 新增模板</Button>
      </div>

      {/* 编辑表单 */}
      {editingId !== null && (
        <div style={{
          background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
          padding: 24, marginBottom: 20,
        }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1a3a6b', marginBottom: 16 }}>
            {isCreating ? '新增风险模板' : `编辑模板：[${form.industry}] ${dimMap[form.dimension_code] || form.dimension_code}`}
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>行业 <span style={req}>*</span></label>
              <input list="industry-suggestions-form" style={inputStyle}
                value={form.industry}
                onChange={(e) => setForm((f) => ({ ...f, industry: e.target.value }))}
                placeholder="如：金融 / 通用" />
              <datalist id="industry-suggestions-form">
                {INDUSTRY_SUGGESTIONS.map((i) => <option key={i} value={i} />)}
              </datalist>
            </div>
            <div>
              <label style={labelStyle}>维度 <span style={req}>*</span></label>
              <select style={inputStyle}
                value={form.dimension_code}
                onChange={(e) => setForm((f) => ({ ...f, dimension_code: e.target.value }))}>
                <option value="">— 选择维度 —</option>
                {dimensions.map((d) => (
                  <option key={d.code} value={d.code}>{d.name}（{d.code}）</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>触发阈值 <span style={req}>*</span></label>
              <input type="number" min="0" max="100" step="1" style={inputStyle}
                value={form.threshold}
                onChange={(e) => setForm((f) => ({ ...f, threshold: parseFloat(e.target.value) || 0 }))} />
              <span style={{ fontSize: 11, color: '#94a3b8' }}>分数 &lt; 此值时触发</span>
            </div>
            <div>
              <label style={labelStyle}>风险等级 <span style={req}>*</span></label>
              <select style={inputStyle} value={form.level}
                onChange={(e) => setForm((f) => ({ ...f, level: e.target.value }))}>
                {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>模板文本 <span style={req}>*</span></label>
            <textarea
              style={{ ...inputStyle, height: 110, resize: 'vertical', fontFamily: 'inherit' }}
              value={form.template_text}
              onChange={(e) => setForm((f) => ({ ...f, template_text: e.target.value }))}
              placeholder={'示例：\n🚨 员工 {name}（{position}）「风险意识」仅 {score} 分，金融场景下涉及客户信息处理违规风险显著。建议：立即取消相关 AI 使用权限，完成《金融业 AI 合规专项培训》后再恢复。'}
            />
            <span style={{ fontSize: 11, color: '#94a3b8' }}>
              占位符: <code style={inlineCode}>{'{name}'}</code> 学员名 ·
              <code style={inlineCode}>{'{score}'}</code> 维度分 ·
              <code style={inlineCode}>{'{position}'}</code> 岗位（缺值自动回退空串）
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
            <input type="checkbox" id="rt-is-active" checked={form.is_active}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
            <label htmlFor="rt-is-active" style={{ fontSize: 13, color: '#475569', cursor: 'pointer' }}>
              启用该模板（停用后该 (行业, 维度) 风险不再触发，老报告原样保留）
            </label>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <Button onClick={handleSave} disabled={saving}>{saving ? '保存中...' : '保存'}</Button>
            <Button variant="secondary" onClick={closeForm}>取消</Button>
          </div>
        </div>
      )}

      {/* 列表 */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>加载中...</div>
        ) : list.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
            暂无模板{filterIndustry || filterDimCode || filterActive ? '（当前筛选）' : ''}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['行业', '维度', '阈值', '等级', '模板预览', '状态', '操作'].map((h) => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map((t, idx) => (
                <tr key={t.id} style={{
                  borderBottom: idx < list.length - 1 ? '1px solid #f1f5f9' : 'none',
                  background: editingId === t.id ? '#f0f7ff' : 'transparent',
                  opacity: t.is_active ? 1 : 0.55,
                }}>
                  <td style={{ ...tdStyle, fontWeight: 600, color: '#1a3a6b' }}>{t.industry}</td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600 }}>{t.dimension_name || dimMap[t.dimension_code] || '—'}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'ui-monospace, monospace' }}>{t.dimension_code}</div>
                  </td>
                  <td style={tdStyle}>{t.threshold}</td>
                  <td style={tdStyle}>
                    <span style={{
                      display: 'inline-block', padding: '2px 10px', borderRadius: 999,
                      fontSize: 12, fontWeight: 700,
                      background: LEVEL_COLOR[t.level]?.bg || '#f1f5f9',
                      color: LEVEL_COLOR[t.level]?.fg || '#475569',
                    }}>{t.level}</span>
                  </td>
                  <td style={{ ...tdStyle, color: '#64748b', maxWidth: 360 }}>
                    <span style={{
                      display: '-webkit-box', WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      whiteSpace: 'pre-wrap',
                    }}>{t.template_text}</span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                      fontSize: 12, fontWeight: 600,
                      background: t.is_active ? '#dcfce7' : '#f1f5f9',
                      color: t.is_active ? '#16a34a' : '#94a3b8',
                    }}>{t.is_active ? '启用' : '停用'}</span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => openEdit(t)} style={btn('#2d5aa0')}>编辑</button>
                      <button onClick={() => handleToggleActive(t)} style={btn(t.is_active ? '#94a3b8' : '#16a34a')}>
                        {t.is_active ? '停用' : '启用'}
                      </button>
                      <button onClick={() => handleDelete(t)} style={btn('#dc2626')}>删除</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p style={{ marginTop: 12, fontSize: 12, color: '#94a3b8' }}>
        共 {list.length} 条。改动后会立即清空风险预警缓存，下次班级看板访问会重算。
      </p>
    </AdminLayout>
  );
}

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 };
const inputStyle = {
  width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8,
  fontSize: 13, outline: 'none', boxSizing: 'border-box',
};
const thStyle = {
  padding: '10px 14px', textAlign: 'left', fontWeight: 600,
  color: '#475569', borderBottom: '1px solid #e2e8f0',
};
const tdStyle = { padding: '12px 14px', verticalAlign: 'middle', color: '#334155' };
const btn = (color) => ({
  padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
  background: color, color: '#fff', fontSize: 12, fontWeight: 600,
});
const inlineCode = {
  fontFamily: 'ui-monospace, monospace', fontSize: 11,
  background: '#f1f5f9', padding: '1px 6px', borderRadius: 4, color: '#1a3a6b',
};
const req = { color: '#dc2626' };
